// Send bypass_cache params on API calls
const DISABLE_SERVER_CACHE = false;

// oxjob #1689 Nuxt trial: Vite/Rollup has no `require`. The store default export
// is now a late-bound proxy, so a plain (circular) import is safe at call time.
import store from '@/store';
import { getUiToken } from '@/uiProvenance';
import { hasBrowser } from '@/utils/env';
const getStore = () => store;

const urlBase = {
    api: "https://api.openalex.org",
    userApi: "https://user.openalex.org",
    // The collections API (oxjob #1515): api.openalex.org/collections, which the proxy
    // routes to users-api's /api/collections. Local users-api serves it at /api.
    collectionsApi: "https://api.openalex.org",
    cvParseApi: "https://openalex-cv-parser.our-research.workers.dev",  // CV parser worker (direct, bypasses proxy)
};

// Use port to change local API endpoints
// Example: npm run serve -- --port 8081

// 8081: Local Elastic API
const devPort = hasBrowser ? window.location.port : '';
if (devPort && parseInt(devPort) === 8081) {
    urlBase.api = "http://localhost:5006";
    console.log("Setting API base URL to local machine (dev use only): " + urlBase.api);

// 8082: Staging Elastic API
} else if (devPort && parseInt(devPort) === 8082) {
    urlBase.api = "https://staging-jump-api.herokuapp.com";
    console.log("Setting API base URL to staging heroku (dev use only): " + urlBase.api);

// 8083: Local User API
} else if (devPort && parseInt(devPort) === 8083) {
    urlBase.userApi = "http://localhost:5106";
    urlBase.collectionsApi = "http://localhost:5106/api";
    console.log("Setting User API base URL to local machine (dev use only): " + urlBase.userApi);

// 8084: Local Elastic & User API
} else if (devPort && parseInt(devPort) === 8084) {
    urlBase.api = "http://localhost:5006";
    console.log("Setting API base URL to local machine (dev use only): " + urlBase.api);
    urlBase.userApi = "http://localhost:5106";
    urlBase.collectionsApi = "http://localhost:5106/api";
    console.log("Setting User API base URL to local machine (dev use only): " + urlBase.userApi);

// 8085: Local CV Parse Server
} else if (devPort && parseInt(devPort) === 8085) {
    urlBase.cvParseApi = "http://localhost:3456";
    console.log("Setting CV Parse API base URL to local machine (dev use only): " + urlBase.cvParseApi);
}

const axiosConfig = (options={}) => {

    const headers = {}
    if (options.userAuth) {
        // api_key (or, pre-cutover, a JWT) Bearer credential for user-API calls
        const token = hasBrowser ? localStorage.getItem("token") : null;
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        // Add impersonation header if impersonating (admin only)
        const impersonatingUserId = hasBrowser ? localStorage.getItem('impersonatingUserId') : null;
        if (impersonatingUserId) {
            headers['X-Impersonate-User'] = impersonatingUserId;
        }
    } else {
        // For OpenAlex API calls, use logged-in user's API key if available
        // No default API key - unauthenticated calls use mailto only
        try {
            const store = getStore();
            const userApiKey = store?.state?.user?.apiKey;
            if (userApiKey) {
                headers.Authorization = `Bearer ${userApiKey}`;
            }
        } catch (e) {
            // Store not yet initialized, no API key header
        }

        // UI-provenance token (oxjob #338 Phase 5a): an unforgeable "this came
        // from the real openalex.org GUI" marker, replacing the spoofable
        // mailto=ui@openalex.org. Attached when available; absence is harmless
        // (the API never gates on it — it only sets the analytics trustedUi
        // tag). Lazy-require mirrors the store import to avoid a circular dep.
        try {
            const uiToken = getUiToken();
            if (uiToken) {
                headers['X-OpenAlex-UI'] = uiToken;
            }
        } catch (e) {
            // Provenance not ready / not initialised — proceed without it.
        }
    }

    if (options.noCache) {
        headers["Cache-Control"] = "no-cache, no-store, must-revalidate";
    }
    return {
        headers: headers
    };
}

// Cloudflare Turnstile sitekey for the signup form (oxjob #252 Phase 4).
// Public value (it's baked into the rendered HTML); paired secret lives in
// the openalex-users-api TURNSTILE_SECRET Heroku config var. Widget is
// scoped to openalex.org / www.openalex.org / localhost.
const TURNSTILE_SITEKEY = "0x4AAAAAADWJQhGbvZ87EmhX";

// Cloudflare Turnstile sitekey for the UI-provenance token (oxjob #338 Phase 5a).
// Separate widget from signup (independent secret/rotation). Public value;
// paired secret = openalex-api-proxy Worker secret TURNSTILE_SECRET. Scoped to
// openalex.org / www.openalex.org (NOT localhost — local dev gets no token and
// is simply tagged untrusted, which is harmless).
const UI_PROVENANCE_SITEKEY = "0x4AAAAAADgYVjKGNQI_Wrxm";

export {
    urlBase,
    axiosConfig,
    DISABLE_SERVER_CACHE,
    TURNSTILE_SITEKEY,
    UI_PROVENANCE_SITEKEY,
};